'use client';

import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/app/Components/AuthProvider';
import { Button } from '@/app/Components/Button';
import { buttonClassName } from '@/app/Components/buttonStyles';
import { getSafeRedirectPath } from '@/app/services/authHelpers';
import { GoogleMark, LoginGatewayShell } from './AuthTransactionShell';

const REDIRECT_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: '/admin', label: 'Admin' }, { prefix: '/ai-assistant', label: 'Ask Scarlet Sync' },
  { prefix: '/class-chat', label: 'Class Chat' }, { prefix: '/cold-email', label: 'Cold Email' },
  { prefix: '/course-lists', label: 'Course Lists' }, { prefix: '/course-list', label: 'Course List' },
  { prefix: '/course-sniper', label: 'Course Sniper' }, { prefix: '/degree-planning', label: 'Degree Planning' },
  { prefix: '/degree-search', label: 'Degree Search' }, { prefix: '/explore', label: 'Explore' },
  { prefix: '/friends', label: 'Friends' }, { prefix: '/info-rutgers', label: 'Info Rutgers' },
  { prefix: '/scheduling', label: 'Scheduling' }, { prefix: '/profile', label: 'Profile' },
  { prefix: '/research-outreach', label: 'Research Outreach' }, { prefix: '/room-availability', label: 'Room Availability' },
  { prefix: '/users', label: 'Users' },
];

const getFallbackRedirectLabel = (path: string) => {
  const [pathname] = path.split('?');
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  if (!firstSegment) return 'your workspace';
  return firstSegment.split('-').filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};

function LoginNotice({ kind, title, description }: { kind: 'loading' | 'error'; title: string; description: string }) {
  const isError = kind === 'error';
  return (
    <div
      aria-live={isError ? 'assertive' : 'polite'}
      className={`rounded-card border px-4 py-3 ${isError ? 'border-[var(--ss-critical)] bg-[var(--ss-critical-bg)]' : 'border-[var(--ss-border)] bg-[var(--ss-surface-subtle)]'}`}
      role={isError ? 'alert' : 'status'}
    >
      <p className={`text-sm font-bold ${isError ? 'text-[var(--ss-critical)]' : 'text-[var(--ss-text)]'}`}>{title}</p>
      <p className="mt-1 text-sm leading-6 text-[var(--ss-text-muted)]">{description}</p>
    </div>
  );
}

function LoginPage() {
  const { signIn, signOut, user, loading: isAuthLoading } = useAuth();
  const searchParams = useSearchParams();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const requestedRedirect = useMemo(() => getSafeRedirectPath(searchParams.get('redirect')), [searchParams]);
  const redirectLabel = useMemo(() => REDIRECT_LABELS.find(({ prefix }) => requestedRedirect.startsWith(prefix))?.label || getFallbackRedirectLabel(requestedRedirect), [requestedRedirect]);
  const isRedirectedFlow = requestedRedirect !== '/scheduling' || searchParams.has('redirect');

  useEffect(() => { document.title = 'Login - Scarlet Sync'; }, []);

  const handleGoogleSignIn = async () => {
    try { setIsLoading(true); setError(''); await signIn(requestedRedirect); }
    catch (caught) { console.error('Google authentication error:', caught); setError(caught instanceof Error ? caught.message : 'Google authentication failed'); }
    finally { setIsLoading(false); }
  };

  const handleSignOut = async () => {
    try { setIsSigningOut(true); setError(''); await signOut(); }
    catch (caught) { console.error('Login page: sign-out error:', caught); setError(caught instanceof Error ? caught.message : 'Could not sign out. Please try again.'); }
    finally { setIsSigningOut(false); }
  };

  const title = isAuthLoading ? 'Checking your session' : user ? 'You are already signed in' : isRedirectedFlow ? `Continue to ${redirectLabel}` : 'Sign in to Scarlet Sync';
  // Unsigned-in Google CTA already says Sign in with Google; skip the redundant subtitle.
  const description = isAuthLoading
    ? 'Restoring your Scarlet Sync session before showing the next action.'
    : user
      ? `Continue to ${redirectLabel}, or sign out to use another account.`
      : undefined;

  return (
    <LoginGatewayShell
      auditReady="login-transaction-ready"
      title={title}
      description={description}
      footer={<div className="flex flex-wrap items-center justify-between gap-3"><Link className="inline-flex min-h-12 items-center font-semibold text-[var(--ss-focus)] hover:underline" href="/support/sign-in-help">Trouble signing in?</Link><Link className="inline-flex min-h-12 items-center font-semibold text-[var(--ss-focus)] hover:underline" href="/contact">Contact support</Link></div>}
    >
      <div className="space-y-5">
        {error ? <LoginNotice kind="error" title="Sign-in needs attention" description={error} /> : null}
        {isAuthLoading ? (
          <LoginNotice kind="loading" title="Restoring your session" description="This usually takes only a moment." />
        ) : user ? (
          <div className="space-y-3">
            <Link className={buttonClassName({ className: 'w-full' })} href={requestedRedirect}>Continue to {redirectLabel}</Link>
            <Button className="w-full" loading={isSigningOut} loadingLabel="Signing out…" onClick={handleSignOut} variant="secondary">Sign out and use another account</Button>
          </div>
        ) : (
          <Button className="min-h-12 w-full" loading={isLoading} loadingLabel="Opening Google…" onClick={handleGoogleSignIn} variant="secondary"><span className="inline-flex items-center gap-2"><GoogleMark />Sign in with Google</span></Button>
        )}
        <p className="text-center text-xs leading-5 text-[var(--ss-text-muted)]">By signing in, you agree to our <Link className="font-semibold text-[var(--ss-focus)] hover:underline" href="/tos">Terms of Service</Link> and <Link className="font-semibold text-[var(--ss-focus)] hover:underline" href="/privacy-policy">Privacy Policy</Link>.</p>
      </div>
    </LoginGatewayShell>
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><LoginPage /></RouteSuspense>; }
